import { formatDate as formatAustralianDate } from '../utils/formatDate';
import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, MapPin, User, Calendar, Search, Plus, AlertCircle, Loader } from 'lucide-react';
import AddFacilityModal from '../components/Forms/AddFacilityModal';
import { useViewMode } from '../contexts/ViewModeContext';
import { facilitiesService, usersService } from '../services';
import { Facility, User as UserType } from '../types';
import './Facilities.css';

// Extended Facility interface for UI display
interface FacilityWithManager extends Facility {
  manager?: UserType;
  assetCount?: number;
  lastInspection?: string;
}

const Facilities: React.FC = () => {
  usePageTitle('Facilities');
  const navigate = useNavigate();
  const location = useLocation();
  const { isFacilityView, effectiveFacilityManagerId } = useViewMode();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [facilities, setFacilities] = useState<FacilityWithManager[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState<string | null>(null);

  // Load facilities and users data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setClientFilter(params.get('client'));
  }, [location.search]);

  useEffect(() => {
    loadData();
  }, [clientFilter, isFacilityView, effectiveFacilityManagerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const facilitiesParams: { client_id?: string; manager_id?: string } = {};
      if (clientFilter) {
        facilitiesParams.client_id = clientFilter;
      }
      if (isFacilityView && effectiveFacilityManagerId) {
        facilitiesParams.manager_id = effectiveFacilityManagerId;
      }
      const hasParams = Object.keys(facilitiesParams).length > 0;

      void usersService.getUsers().then(setUsers).catch(() => {});
      const facilitiesData = await facilitiesService.getFacilities(hasParams ? facilitiesParams : undefined);
      setFacilities(facilitiesData.map(facility => ({
        ...facility,
        assetCount: facility.asset_count || 0,
        lastInspection: facility.last_inspection_at || undefined,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load facilities');
      console.error('Failed to load facilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFacility = async (facilityData: any) => {
    try {
      // Transform the form data to match API requirements
      const createData = {
        name: facilityData.name,
        jurisdiction: facilityData.jurisdiction || null,
        facility_category: facilityData.facility_category || null,
        address: facilityData.address,
        client_id: facilityData.clientId,
        manager_id: facilityData.managerId
      };

      const newFacility = await facilitiesService.createFacility(createData);

      // Add additional staff assignments if any
      if (facilityData.additionalStaff?.length > 0) {
        await Promise.all(
          facilityData.additionalStaff.map((staff: any, i: number) =>
            facilitiesService.addFacilityStaff(newFacility.id, {
              user_id: staff.userId,
              role: staff.role || undefined,
              order: i + 1,
            }).catch((err: unknown) => {
              console.error(`Failed to add staff ${staff.userName}:`, err);
            })
          )
        );
      }

      const manager = users.find(user => user.id === newFacility.manager_id);

      // Add to local state
      setFacilities(prev => [...prev, {
        ...newFacility,
        manager,
        assetCount: 0,
        lastInspection: undefined
      }]);

      return newFacility.id;
    } catch (err) {
      console.error('Failed to create facility:', err);
      // The modal will handle its own error display
      throw err;
    }
  };

  const filteredFacilities = facilities.filter(facility =>
    facility.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    facility.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    users.find(user => user.id === facility.manager_id)?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (facility: FacilityWithManager) => {
    const assetCount = facility.assetCount || 0;

    if (assetCount === 0) {
      return { class: 'badge badge-secondary', label: 'No Assets' };
    } else if (assetCount >= 3) {
      return { class: 'badge badge-success', label: 'Active' };
    } else if (assetCount >= 1) {
      return { class: 'badge badge-warning', label: 'Minimal' };
    } else {
      return { class: 'badge badge-secondary', label: 'Inactive' };
    }
  };

  const getTypeIcon = (type: string) => {
    // For demo purposes, using the same icon. In real app, would have different icons per type
    return Building2;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return formatAustralianDate(dateString);
  };

  if (loading) {
    return (
      <div className="facilities">
        <div className="loading-container">
          <Loader className="loading-spinner" size={32} />
          <p>Loading facilities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="facilities">
        <div className="error-container">
          <AlertCircle className="error-icon" size={32} />
          <h3>Failed to load facilities</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="facilities">
      <div className="facilities-header">
        <div className="header-content">
          <h1>Facilities</h1>
          <p>Manage and monitor your water infrastructure facilities</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={20} />
          Add Facility
        </button>
      </div>

      <div className="facilities-controls">
        <div className="search-filters">
          <div className="search-container">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search facilities..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="view-controls">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      <div className="facilities-summary">
        <p>
          {filteredFacilities.length} of {facilities.length} facilities
          {clientFilter ? ' (filtered by selected client)' : ''}
        </p>
      </div>

      <div className={`facilities-content ${viewMode}`}>
        {filteredFacilities.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No facilities found</h3>
            <p>
              {facilities.length === 0
                ? "Start by adding your first facility"
                : "Try adjusting your search terms"
              }
            </p>
            {facilities.length === 0 && (
              <button
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                Add Facility
              </button>
            )}
          </div>
        ) : (
          filteredFacilities.map((facility) => {
            const TypeIcon = getTypeIcon('default');
            const statusBadge = getStatusBadge(facility);

            return (
              <div
                key={facility.id}
                className="facility-card card"
                onClick={() => navigate(`/facilities/${facility.id}`)}
              >
                <div className="facility-header">
                  <div className="facility-icon">
                    <TypeIcon size={24} />
                  </div>
                  <div className="facility-status">
                    <span className={statusBadge.class}>
                      {statusBadge.label}
                    </span>
                  </div>
                </div>

                <div className="facility-content">
                  <h3 className="facility-name">{facility.name}</h3>

                  <div className="facility-details">
                    <div className="detail-item">
                      <MapPin size={16} />
                      <span>{facility.address}</span>
                    </div>

                    <div className="detail-item">
                      <User size={16} />
                      <span>{users.find(user => user.id === facility.manager_id)?.name || 'No manager assigned'}</span>
                    </div>

                    <div className="detail-item">
                      <Calendar size={16} />
                      <span>Last inspection: {formatDate(facility.lastInspection)}</span>
                    </div>
                  </div>

                  <div className="facility-stats">
                    <div className="stat">
                      <span className="stat-value">{facility.assetCount || 0}</span>
                      <span className="stat-label">Assets</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {new Date(facility.created_at).getFullYear()}
                      </span>
                      <span className="stat-label">Created</span>
                    </div>
                  </div>
                </div>

                <div className="facility-actions">
                  <button
                    className="btn btn-ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/facilities/${facility.id}`);
                    }}
                  >
                    View Details
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/facilities/${facility.id}/inspection`);
                    }}
                  >
                    Schedule Inspection
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <AddFacilityModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddFacility}
      />
    </div>
  );
};

export default Facilities;
